/**
 * This file is a base store for all model items.
 * The base store contains common methods like
 * create, delete, update.
 * Furthermore, getters like ids, length...
 */

import {
  computed,
  reactive,
  shallowReadonly,
  toValue,
} from "vue";
import {
  difference,
  map,
  without,
  cloneDeep,
  mapValues,
  defaults,
  mapKeys
} from "lodash-es";
import {
  acceptHMRUpdate,
  defineStore,
} from "pinia";
import eventHub from "@/helpers/utility/Events";
import {
  assignDeep,
  cloneInto,
  defaultsDeepPreserveArrays
} from "@/helpers/utility/CollectionFunctions";

import { getStoreSortFunction } from "./ItemStore";
import { migrateStoreItem } from "./itemStoreMigration";

import type { StoreItem } from "./StoreItem";
import type { ModuleNamespace } from "vite/types/hot.js";
import type { Subset } from "@/helpers/utility/Type";
import type { ItemStoreState } from "./ItemStore";
import type { _StoreWithState, Store, StoreDefinition } from "pinia";
import type { ComputedRef, MaybeRefOrGetter, UnwrapRef } from "vue";
import { splitUid, type Uid } from "@/helpers/utility/Id";

export type ItemStoreEvents<T extends StoreItem> = {
  created: (
    this: Store<string, ItemStoreState<T>>,
    item: T,
    store: Store<string, ItemStoreState<T>>
  ) => Promise<void>;
  beforeCreate?: (
    this: Store<string, ItemStoreState<T>>,
    item: Subset<T> & { id: T["id"]; uid: T["uid"]; createdAt: number },
    extraData?: object
  ) => Promise<Subset<T> & { id: T["id"]; createdAt: number; uid: T["uid"] }>;

  /**
   * Return any properties to change on the object.
   */
  beforeUpdate?: (
    this: Store<string, ItemStoreState<T>>,
    item: T,
    data: Subset<T>,
    oldItem: T
  ) => object;

  afterUpdate?: (
    this: Store<string, ItemStoreState<T>>,
    item: T,
    updatedData: Subset<T>,
    oldItem: T
  ) => void;

  beforeDuplicate?: (
    this: Store<string, ItemStoreState<T>>,
    duplicatedItem: Subset<T>,
    changes: Subset<T>,
    sourceItem: T
  ) => Promise<Subset<T>>;

  afterDelete?: (
    this: Store<string, ItemStoreState<T>>,
    item: T,
    store: Store<string, ItemStoreState<T>>,
    extraOptions: Record<string, unknown>
  ) => Promise<unknown>;
  canBeDeleted?: (
    this: Store<string, ItemStoreState<T>>,
    item: T,
    store: Store<string, ItemStoreState<T>>,
    extraOptions: Record<string, unknown>
  ) => Promise<boolean>;
};

function makeItemState<T extends StoreItem>(itemStructure: T) {
  const s: ItemStoreState<T> = {
    // items are ordered by their id.
    items: {},

    order: [],

    count: 0,

    type: "objectstore",
    itemStructure: cloneDeep(itemStructure)
  };

  return s;
}

function makeItemGetters<T extends StoreItem>() {
  type _StoreWithGetters<G> = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [k in keyof G]: G[k] extends (...args: any[]) => infer R
      ? R
      : UnwrapRef<G[k]>;
  };

  function wrapGetters<G>(getters: G & ThisType<_StoreWithGetters<G>>): G {
    return getters;
  }

  const g = wrapGetters({
    length(): number {
      return this.orderedItems.length;
    },

    /**
     * All ids of the existing objects, as Array.
     */
    ids(): readonly T["id"][] {
      return this.orderedItems.map((item) => item.id);
    },

    orderedItems(state: { order: number[]; items: { [itemId: number]: T } }) {
      const orderedItems: T[] = [];
      for (const id of state.order) {
        if (id == null) continue;
        if (id in state.items) {
          if (state.items[id] == null) continue;
          orderedItems.push(state.items[id]);
        }
      }
      return shallowReadonly(orderedItems);
    },

    getByIds(state: { items: { [itemId: number]: T } }) {
      /**
       * Get a dictionary of items by their ids.
       * @param ids - array of item ids
       */
      return (ids: Iterable<T["id"]>) => {
        if (ids == null) return {};

        const result: { [n: number]: T } = {};
        for (const id of ids) {
          if (id in state.items) {
            result[id] = state.items[id];
          }
        }
        return result;
      };
    },

    getArrayByIds(state: { items: { [itemId: number]: T } }) {
      /**
       * Get an array of items by their ids. Keeps the order of the ids.
       * @param ids - array of item ids
       */
      return (ids: T["id"][]): T[] =>
        ids.reduce((items: T[], id) => {
          if (id == null) return items;
          if (state.items[id]) items.push(state.items[id]);
          return items;
        }, []);
    },

    getArrayByUids(state: { items: { [itemId: number]: T } }) {
      /**
       * Get an array of items by their uids. Keeps the order of the uids.
       * @param uids - array of item uids
       */
      return (uids: T["uid"][]): T[] =>
        uids.reduce((items: T[], uid) => {
          if (uid == null) return items;
          const item = this.byUid[uid];
          if (item) items.push(item);
          return items;
        }, []);
    },

    sortFunction(state: ItemStoreState<T>) {
      /**
       * Sort the items by a function.
       * @param fn - sorting function
       */
      return getStoreSortFunction(state);
    },

    /**
     * Mirror state.items for type hinting in getter functions.
     * Use state.items where possible instead (as long as the type system works).
     */
    byId(state: { items: { [itemId: string]: T } }): { [itemId: string]: T } {
      return state.items;
    },

    byUid(state: { items: { [itemId: number]: T } }): { [uid: string]: T } {
      return mapKeys(state.items, (v) => v.uid);
    }
  });

  return g;
}
type ItemStoreGetters<T extends StoreItem> = ReturnType<
  typeof makeItemGetters<T>
>;

function makeItemActions<T extends StoreItem>(
  itemStructure: T,
  _events: Partial<ItemStoreEvents<T>> = {}
) {
  const events: ItemStoreEvents<T> = Object.assign(
    {
      created() {},
      beforeCreate: null,
      afterDelete: null,
      canBeDeleted: null
    },
    _events
  );

  function wrapActions<A>(
    actions: A &
      ThisType<A & Store<string, ItemStoreState<T>, ItemStoreGetters<T>, A>>
  ): A {
    return actions;
  }

  return wrapActions({
    /**
     * Create a new item
     */
    async create(itemData: Subset<T> = {}, extraData?: object): Promise<T> {
      const id = this.count++;

      let item = {
        ...itemData,

        id: id,

        // set the item id to the latest available id
        // and increase the counter afterwards
        uid: `${this.itemStructure.type}_${id}`,

        // set the creation date
        createdAt: Date.now()
      };

      // do something with the item before creating the entry
      if (events.beforeCreate) {
        eventHub.emit(`${this.itemStructure.type}-before-create`, item);
        item = await events.beforeCreate.call(this, item, extraData);
      }

      this.update(item);

      const newEntry = this.items[item.id];
      await events.created.call(this, newEntry, this);

      // insert the id of the new item in the order array
      this.updateOrder();

      eventHub.emit(`${this.itemStructure.type}-created`, newEntry);

      return newEntry;
    },

    /**
     * Create many items at once
     */
    createMany(items: Subset<T>[]) {
      const newItems = [];

      for (const item of items) {
        newItems.push(this.create(item));
      }

      return Promise.all(newItems);
    },

    /**
     * Delete an item
     * @param options
     * @param options.id - id of the element
     * @param options.instant - delete without further questions
     */
    async delete({
      id = null,
      uid = null,
      instant = false
    }: { id?: T["id"] | null; uid?: Uid<T["type"]> | null; instant?: boolean } = {}, extraOptions: Record<string, unknown> = {}) {
      if (id == null && uid == null) {
        console.error("No id or uid passed.");
        return;
      }

      if (id == null) id = splitUid(uid!).id;
      if (id == null) {
        console.error("No valid id or uid passed.");
      }

      // get a fresh reference of the item from the store
      // (makes sure that all required properties exist)
      const item = this.items[id];

      if (item == null) {
        throw new Error(`${this.itemStructure.type} ${id} does not exist.`);
      }

      // hook event to prevent deletion on conditions
      if (!instant && events.canBeDeleted) {
        if (!(await events.canBeDeleted.call(this, item, this, extraOptions))) {
          console.error("deletion not allowed.");
          return false;
        }
      }

      const type = item.type;

      // deselect object if selected
      // const selectionStore = useSelectionStore();
      // if (selectionStore.contains(item)) {
      //   selectionStore.remove(item);
      // }

      eventHub.emit(`${this.itemStructure.type}-before-delete`, item);

      // remove id from order array
      this.order = without(this.order, id);
      delete this.items[id];

      if (events.afterDelete) {
        await events.afterDelete.call(this, item, this, extraOptions);
      }

      eventHub.emit(`${this.itemStructure.type}-deleted`, item);

      console.log(`deleted ${type} ${item.id}`);

      return true;
    },

    /**
     * Delete many items at once
     */
    async deleteMany(items: { id: T["id"] }[], extraOptions: Record<string, unknown> = {}) {
      for (const item of items) {
        await this.delete(item, extraOptions);
      }
    },

    /**
     * Update the values of an existing item
     * @param updates Data of one item to change, must include id.
     */
    update(updates: Subset<T> & Required<Pick<T, "id">>, extraOptions?: { _undoable?: boolean }) {
      const id: T["id"] = updates.id;

      if (id == null) {
        console.error("ID not set!");
        throw new Error("Items without id can't be modified.");
      }

      let updatedItem: T;

      // EXISTING ITEM:
      // pass over the data from the old entry if it exists.
      const oldEntry: T | null = cloneDeep(this.items[id] ?? null);
      if (oldEntry != null) {
        updatedItem = assignDeep<T>(cloneDeep(oldEntry), updates) as T; // defaultsDeepPreserveArrays(data, oldEntry);
      }
      // NEW ITEM:
      // otherwise, fill the remainder with the default data for one element.
      else {
        updatedItem = assignDeep<T>(cloneDeep(itemStructure), updates) as T; // defaultsDeepPreserveArrays(data, itemStructure);
        updatedItem.createdAt = Date.now();
      }

      let additionalUpdates: Subset<T> = {};

      // get additional changes before updating the entry
      if (events.beforeUpdate) {
        additionalUpdates =
          events.beforeUpdate.call(this, updatedItem, updates, this.items[id]) ||
          {};
      }

      // create the item if it didn't exist up till now.
      if (oldEntry == null) {
        Object.assign(updatedItem, additionalUpdates);
        this.items[id] = updatedItem;
      }
      // otherwise, only update the properties
      else {
        const totalUpdates = cloneDeep(updates);
        cloneInto(totalUpdates, additionalUpdates);
        assignDeep(this.items[id], totalUpdates as Subset<T>);
      }

      if (events.afterUpdate) {
        events.afterUpdate.call(this, this.items[id], updates, oldEntry);
      }

      eventHub.emit(
        `${this.itemStructure.type}-updated`,
        this.items[id],
        oldEntry
      );

      return this.items[id];
    },

    updateKey<K extends keyof T>(id: T["id"], key: K, value: T[K]) {
      const change: Partial<T> = {};
      change[key] = value;
      change.id = id;
      return this.update(change as Partial<T> & { id: T["id"] });
    },

    /**
     * Update many items at once (does not create items)
     */
    updateMany(items: (Subset<T> & Required<Pick<T, "id">>)[]) {
      const changedItems = [];
      for (const item of items) {
        const id: number = item.id;
        if (!this.items?.[id])
          throw new Error(`${itemStructure.type} ${id} does not exist.`);

        // pass over the data from the old entry if it exists.
        const updatedItem = defaultsDeepPreserveArrays(item, this.items[id]);

        // get additional changes before updating the entry
        if (events.beforeUpdate) {
          const changes =
            events.beforeUpdate.call(this, updatedItem, item, this.items[id]) ||
            {};
          Object.assign(updatedItem, changes);
        }

        Object.assign(this.items[id], updatedItem);

        changedItems.push(this.items[id]);
      }
      return changedItems;
    },

    async duplicate(id: T["id"], changes: Subset<T> = {}) {
      const item: Partial<T> = cloneDeep(this.items[id]);
      delete item.id;
      delete item.uid;
      delete item.createdAt;

      cloneInto(item, changes);

      if (events.beforeDuplicate) {
        const additionalChanges = await events.beforeDuplicate.call(this, item as T, changes, this.items[id]) || {};
        cloneInto(item, additionalChanges);
      }

      const newItem = await this.create(item);

      // insert the id of the new item after the original item
      // in the order array
      const newOrder = without(this.order, newItem.id);
      const index = newOrder.indexOf(id);
      newOrder.splice(index + 1, 0, newItem.id);
      this.order = newOrder;

      return newItem;
    },

    /**
     * create order entries for the items that don't have an id entry in the order array
     */
    updateOrder() {
      const ids: number[] = map(this.items, "id");
      const notIncluded = difference(ids, this.order);
      if (notIncluded.length > 0) {
        this.order = this.order.concat(notIncluded);
      }
      // remove all elements from the order that do not exist
      const notExisting = difference(this.order, ids);
      if (notExisting.length > 0) {
        this.order = without(this.order, ...notExisting);
      }
    },
    /**
     * Change the order of items in the store.
     * You may pass a subset of the items to reorder,
     * the rest will be appended at the end.
     */
    saveOrder(orderArray: number[]) {
      const oldOrder = this.order.slice();
      const oldNotIncluded = difference(oldOrder, orderArray);
      const reordered = orderArray.concat(oldNotIncluded);
      const ids = map(this.items, "id");
      const notIncluded = difference(reordered, ids);
      this.order = reordered.concat(notIncluded);
    },
    setState(changes: Partial<ItemStoreState<T>>) {
      this.$patch((state) => {
        for (const key in changes) {
          state[key] = changes[key as keyof ItemStoreState<T>];
        }
      });
    },

    importItem(oldItem: object) {
      const oldItemData = cloneDeep(oldItem);

      // prevent any id-based changes
      if ("id" in oldItemData) delete oldItemData.id;
      if ("uid" in oldItemData) delete oldItemData.uid;

      const newItem = migrateStoreItem<T>(
        oldItem,
        this.itemStructure as T,
        this as ItemStoreState<T>,
        {
          create: this.create
        }
      );
      return this.create(newItem);
    }
  });
}

export type ItemStoreActions<T extends StoreItem> = ReturnType<
  typeof makeItemActions<T>
>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StateTree = Record<string, any>;

type GettersTree<S, T extends StoreItem> = Record<
  string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ((state: ItemStoreState<T> & S) => any) | (() => any)
>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
// type ActionsTree = Record<string, (...args: any[]) => any | (() => any)>;

type _StoreWithGetters<G> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k in keyof G]: G[k] extends (...args: any[]) => infer R
    ? R
    : UnwrapRef<G[k]>;
};

type ItemStoreOptions<
  T extends StoreItem,
  S extends StateTree,
  G extends GettersTree<S, T>,
  A, // extends ActionsTree,
  E extends Partial<ItemStoreEvents<T>>
> = {
  state?: (baseStoreState: ItemStoreState<T>) => S;

  getters?: 
    ThisType<
      ItemStoreState<T> & S & _StoreWithGetters<ItemStoreGetters<T> & G>
    > & G;

  actions?: ThisType<
      ItemStoreActions<T> &
        ItemStoreState<T> &
        S &
        _StoreWithState<string, ItemStoreState<T> & S, G, A> &
        _StoreWithGetters<ItemStoreGetters<T> & G> &
        { super: SuperActions<T> }
    > & A;

  events?: E;
};

type Getters<E> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k in keyof E]: E[k] extends (...args: any[]) => infer R
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (state?: any) => R
    : never;
};

type SuperActions<T extends StoreItem> = ItemStoreActions<T>;

export function defineItemStore<
  Id extends string,
  T extends StoreItem,
  S extends StateTree,
  G extends GettersTree<S, T>,
  A, // extends ActionsTree,
  E extends Partial<ItemStoreEvents<T>>
>(id: Id, itemStructure: T, options: ItemStoreOptions<T, S, G, A, E>) {
  const baseState = makeItemState(itemStructure);
  const state = (options.state?.(baseState) || {}) as S;
  const getters = (options.getters || {}) as Getters<G>;
  const actions = (options.actions || {}) as A;

  const baseActions = makeItemActions<T>(itemStructure, options.events || {});

  const superActions: SuperActions<T> = baseActions;

  const useStore = defineStore(id, {
    state: () => ({
      ...baseState,
      ...state
    }),

    getters: {
      ...makeItemGetters<T>(),
      super() {
        const actions: Record<string, Function> = {};
        for (const k in superActions) {
          const key = k as keyof SuperActions<T>;
          actions[key] = superActions[key].bind(this);
        }
        return actions as SuperActions<T>;
      },
      ...getters
    },

    actions: {
      ...baseActions,
      ...actions
    } as (Omit<ItemStoreActions<T>, keyof A> & A)
  });

  return useStore;
}

export function useStoreItem<
  T extends StoreItem,
  S extends StoreDefinition<
    string,
    ItemStoreState<T>,
    ItemStoreGetters<T>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any
  >,
  E extends object
>(
  _itemStructure: T,
  useStore: S,
  getExtraProps?: (item: ComputedRef<T | null>, store: ReturnType<S>) => E
) {
  return <Id extends number | Uid<string> | null>(id: MaybeRefOrGetter<Id>) => {
    const store = useStore() as Store<
      string,
      ItemStoreState<T>,
      ItemStoreGetters<T>,
      ItemStoreActions<T>
    >;

    type ReturnedItem<Id> = Id extends null ? T | null : T;

    function getId(): T["id"] | null {
      const _id = toValue(id);
      if (_id == null) return null;
      if (typeof _id === "number") return _id;
      if (typeof _id === "string") return splitUid(_id).id;
      console.warn("Invalid id type passed to useStoreItem:", _id);
      return null;
    }

    const item = computed(() => {
      const _id = getId();
      if (_id == null) return null as ReturnedItem<Id>;
      return store.items[_id] as ReturnedItem<Id>;
    });

    function updateKey<K extends keyof T>(key: K, value: T[K]) {
      const _id = getId();
      if (_id == null) {
        throw new Error("Can't update key of item without id.");
      }
      return store.updateKey(_id, key, value);
    }

    function update(data: Subset<T>) {
      const _id = getId();
      if (_id == null) {
        throw new Error("Can't update key of item without id.");
      }
      return store.update({ ...data, id: _id });
    }

    function deleteItem() {
      return store.delete({ id: getId() });
    }

    const extraProps =
      (getExtraProps?.(item, store as ReturnType<S>) as E) || {};

    const context = {
      item,
      [`${store.itemStructure.type}`]: item,
      updateKey,
      update,
      delete: deleteItem,
      getId,

      ...extraProps
    } as {
      [k in T["type"]]: typeof item;
    } & {
      item: typeof item;
      updateKey: typeof updateKey;
      update: typeof update;
      delete: typeof deleteItem;
      getId: typeof getId;
    } & {
      [k in keyof E]: E[k];
    };

    return reactive({ ...context });
  };
}

/**
 * Creates an _accept_ function to pass to `import.meta.hot` in Vite applications.
 *
 * @example
 * ```js
 * const useUser = defineStore(...)
 * if (import.meta.hot) {
 *   import.meta.hot.accept(acceptHMRUpdate(useUser, import.meta.hot))
 * }
 * ```
 *
 * @param initialUseStore - return of the defineStore to hot update
 * @param hot - `import.meta.hot`
 */
export function acceptHotItemStore<
  Id extends string,
  S extends StateTree,
  G,
  A
>(
  initialUseStore: StoreDefinition<Id, S, G, A> & {
    _pinia?: Store & {
      _s: {
        get: (storeId: string) => Store<string, ItemStoreState>;
      };
    };
  },
  hot: {
    data: {
      pinia?: Store & {
        _s: {
          get: (storeId: string) => Store<string, ItemStoreState>;
        };
      };
    };
  }
) {
  const acceptPiniaStoreUpdate = acceptHMRUpdate(
    initialUseStore as StoreDefinition,
    hot
  );

  return (newModule?: ModuleNamespace) => {
    const pinia = hot.data.pinia || initialUseStore._pinia;
    if (!pinia) return;

    const _s = pinia._s;

    // Save the states of the store before updating.
    const originalStates: { [moduleName: string]: ItemStoreState } = {};

    for (const exportName in newModule) {
      const useStore = newModule[exportName];
      const existingStore = _s.get(useStore.$id);
      if (existingStore == null) {
        console.warn("no store found", exportName, useStore.$id);
        continue;
      }
      originalStates[useStore.$id] = cloneDeep(existingStore?.$state ?? {});
    }

    // Update the pinia store.
    acceptPiniaStoreUpdate(newModule);

    // Restore the value of "items" in the store
    // to the items before the update.
    for (const storeId in originalStates) {
      const store: Store<string, ItemStoreState> = _s.get(storeId);
      if (!store) continue;
      const newItemStructure = store.itemStructure;

      const newItems = mapValues(
        originalStates[storeId].items,
        (originalItem) => {
          const newItem = cloneDeep(newItemStructure);
          return defaults({}, originalItem, newItem);
        }
      );

      store.items = newItems;
    }
  };
}
